import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Chart from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import moment from 'moment';
import { getPurchaseList } from '../services/DashboardServices';
import {
  getFinancialYearStartYear,
  getFinancialYearBounds,
} from './MarketingSalesChart';
import './MarketingSalesChart.css';

Chart.register(ChartDataLabels);

const BAR_COLORS = [
  '#e67e22', '#3498db', '#9b59b6', '#1abc9c', '#f39c12',
  '#e74c3c', '#2ecc71', '#34495e', '#16a085', '#8e44ad',
];

const VIEW_OPTIONS = [
  { id: 'vendor', label: 'Vendor-wise' },
  { id: 'material', label: 'Material-wise' },
  { id: 'day', label: 'Date-wise' },
  { id: 'month', label: 'Month-wise' },
];

const RAW_MATERIAL_CATEGORY = 'Raw Material';

function parsePurchaseDate(value) {
  if (!value) return null;
  const parsed = moment(value, ['DD-MM-YYYY', 'YYYY-MM-DD', moment.ISO_8601], true);
  return parsed.isValid() ? parsed : null;
}

function buildFinancialYearOptions(refMoment) {
  const currentStart = getFinancialYearStartYear(refMoment);
  return [currentStart - 1, currentStart, currentStart + 1].map((startYear) => {
    const bounds = getFinancialYearBounds(startYear);
    return { startYear, label: bounds.label };
  });
}

function filterRawMaterials(purchaseData) {
  const rows = purchaseData || [];
  const raw = rows.filter((row) => row.material_category === RAW_MATERIAL_CATEGORY);
  if (raw.length) return raw;
  return rows.filter((row) => row.material_category !== 'Diesel');
}

function bucketTopN(rows, topN = 8) {
  if (rows.length <= topN) return rows;
  const top = rows.slice(0, topN);
  const othersQty = rows.slice(topN).reduce((sum, row) => sum + row.qty, 0);
  if (othersQty > 0) {
    top.push({ name: 'Others', qty: Number(othersQty.toFixed(2)), sortKey: null });
  }
  return top;
}

function aggregateByKey(purchaseData, keyFn, { sortByQty = true, topN = null } = {}) {
  const totals = {};
  const meta = {};
  (purchaseData || []).forEach((row) => {
    const info = keyFn(row);
    if (!info) return;
    const { key, label, sortKey } = info;
    totals[key] = (totals[key] || 0) + (Number(row.received_qty) || 0);
    if (!meta[key]) {
      meta[key] = { label, sortKey };
    }
  });

  let rows = Object.entries(totals).map(([key, qty]) => ({
    name: meta[key].label,
    qty: Number(qty.toFixed(2)),
    sortKey: meta[key].sortKey,
  }));

  if (sortByQty) {
    rows.sort((a, b) => b.qty - a.qty);
  } else {
    rows.sort((a, b) => {
      if (a.sortKey == null && b.sortKey == null) return 0;
      if (a.sortKey == null) return 1;
      if (b.sortKey == null) return -1;
      if (a.sortKey < b.sortKey) return -1;
      if (a.sortKey > b.sortKey) return 1;
      return 0;
    });
  }

  if (topN) {
    rows = bucketTopN(rows, topN);
  }
  return rows;
}

function aggregateByPeriodAndMaterial(purchaseData, getPeriod) {
  const totals = {};
  const meta = {};

  filterRawMaterials(purchaseData).forEach((row) => {
    const period = getPeriod(row);
    if (!period) return;
    const material = String(row.item || '').trim() || 'Unspecified';
    const key = `${period.key}|${material}`;
    totals[key] = (totals[key] || 0) + (Number(row.received_qty) || 0);
    if (!meta[key]) {
      meta[key] = {
        periodKey: period.key,
        periodLabel: period.label,
        material,
        sortKey: `${period.sortKey}|${material.toLowerCase()}`,
      };
    }
  });

  return Object.entries(totals).map(([key, qty]) => ({
    periodKey: meta[key].periodKey,
    periodLabel: meta[key].periodLabel,
    material: meta[key].material,
    name: meta[key].material,
    qty: Number(qty.toFixed(2)),
    sortKey: meta[key].sortKey,
  })).sort((a, b) => {
    if (a.sortKey < b.sortKey) return -1;
    if (a.sortKey > b.sortKey) return 1;
    return 0;
  });
}

export function groupRowsByPeriod(rows) {
  const groups = [];
  const groupMap = new Map();

  (rows || []).forEach((row) => {
    if (!groupMap.has(row.periodKey)) {
      const group = {
        periodKey: row.periodKey,
        periodLabel: row.periodLabel,
        items: [],
        totalQty: 0,
      };
      groupMap.set(row.periodKey, group);
      groups.push(group);
    }
    const group = groupMap.get(row.periodKey);
    group.items.push(row);
    group.totalQty += Number(row.qty) || 0;
  });

  return groups.map((group) => ({
    ...group,
    totalQty: Number(group.totalQty.toFixed(2)),
  }));
}

export function aggregatePurchaseByView(purchaseData, view) {
  if (view === 'vendor') {
    return aggregateByKey(
      filterRawMaterials(purchaseData),
      (row) => {
        const vendor = String(row.vendor || '').trim() || 'Unassigned';
        const material = String(row.item || '').trim() || 'Unspecified';
        return {
          key: `${vendor}|${material}`,
          label: `${vendor} · ${material}`,
          sortKey: `${vendor.toLowerCase()}|${material.toLowerCase()}`,
        };
      },
      { sortByQty: true },
    );
  }

  if (view === 'material') {
    return aggregateByKey(
      filterRawMaterials(purchaseData),
      (row) => {
        const name = String(row.item || '').trim() || 'Unspecified';
        return { key: name, label: name, sortKey: name.toLowerCase() };
      },
      { sortByQty: true },
    );
  }

  if (view === 'day') {
    return aggregateByPeriodAndMaterial(purchaseData, (row) => {
      const dt = parsePurchaseDate(row.date);
      if (!dt) return null;
      return {
        key: dt.format('YYYY-MM-DD'),
        label: dt.format('DD MMM'),
        sortKey: dt.format('YYYY-MM-DD'),
      };
    });
  }

  return [];
}

export function aggregatePurchaseMonthMaterialWise(purchaseData, fyStartYear) {
  const { fyStart, fyEnd, label } = getFinancialYearBounds(fyStartYear);
  const inFy = (purchaseData || []).filter((row) => {
    const dt = parsePurchaseDate(row.date);
    if (!dt) return false;
    return !dt.isBefore(fyStart, 'day') && !dt.isAfter(fyEnd, 'day');
  });

  return {
    fyLabel: label,
    rows: aggregateByPeriodAndMaterial(inFy, (row) => {
      const dt = parsePurchaseDate(row.date);
      if (!dt) return null;
      return {
        key: dt.format('YYYY-MM'),
        label: dt.format('MMM'),
        sortKey: dt.format('YYYY-MM'),
      };
    }),
  };
}

function getViewMeta(view, fyLabel) {
  switch (view) {
    case 'vendor':
      return {
        title: 'Vendor-wise Purchase Performance',
        subtitle: 'Raw materials received — vendor and material',
      };
    case 'material':
      return {
        title: 'Material-wise Purchase Performance',
        subtitle: 'All raw materials — received qty',
      };
    case 'month':
      return {
        title: 'Month-wise Purchase Performance',
        subtitle: `Raw materials per month · Apr–Mar · ${fyLabel || 'Financial year'}`,
      };
    default:
      return {
        title: 'Date-wise Purchase Performance',
        subtitle: 'All raw materials purchased on each date',
      };
  }
}

function PurchaseChart({
  purchaseData,
  cookies,
  plantId,
  referenceDate,
}) {
  const [view, setView] = useState('vendor');
  const [fyStartYear, setFyStartYear] = useState(() => getFinancialYearStartYear(referenceDate || moment()));
  const [fyPurchaseData, setFyPurchaseData] = useState([]);
  const [fyLoading, setFyLoading] = useState(false);
  const [fyError, setFyError] = useState('');
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  const fyOptions = useMemo(
    () => buildFinancialYearOptions(referenceDate || moment()),
    [referenceDate],
  );

  useEffect(() => {
    setFyStartYear(getFinancialYearStartYear(referenceDate || moment()));
  }, [referenceDate]);

  const loadFyPurchase = useCallback(() => {
    if (!cookies?.myToken || !plantId || view !== 'month') return;
    const { fyStart, fyEnd } = getFinancialYearBounds(fyStartYear);
    setFyLoading(true);
    setFyError('');
    getPurchaseList(
      cookies,
      plantId,
      fyStart.format('DD-MM-YYYY'),
      fyEnd.format('DD-MM-YYYY'),
    )
      .then((response) => {
        setFyPurchaseData(response?.purchase_list || []);
      })
      .catch((err) => {
        setFyPurchaseData([]);
        setFyError(err?.response?.data?.detail || err?.message || 'Failed to load purchase data');
      })
      .finally(() => setFyLoading(false));
  }, [cookies, plantId, view, fyStartYear]);

  useEffect(() => {
    loadFyPurchase();
  }, [loadFyPurchase]);

  const monthAgg = useMemo(() => {
    if (view !== 'month') return null;
    return aggregatePurchaseMonthMaterialWise(fyPurchaseData, fyStartYear);
  }, [view, fyPurchaseData, fyStartYear]);

  const rows = useMemo(() => {
    if (view === 'month') return monthAgg?.rows || [];
    return aggregatePurchaseByView(purchaseData, view);
  }, [purchaseData, view, monthAgg]);

  const groupedRows = useMemo(() => {
    if (view !== 'month' && view !== 'day') return [];
    return groupRowsByPeriod(rows);
  }, [rows, view]);

  const useGroupedView = view === 'month' || view === 'day';

  const totalQty = useMemo(
    () => rows.reduce((sum, row) => sum + (Number(row.qty) || 0), 0),
    [rows],
  );
  const meta = getViewMeta(view, monthAgg?.fyLabel);
  const showChart = rows.length > 0 && !(view === 'month' && fyLoading);

  useEffect(() => {
    let cancelled = false;
    let frameId = 0;

    const destroyChart = () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };

    if (!showChart || useGroupedView) {
      destroyChart();
      return undefined;
    }

    frameId = window.requestAnimationFrame(() => {
      if (cancelled || !canvasRef.current) return;
      destroyChart();

      const ctx = canvasRef.current.getContext('2d');
      if (!ctx) return;

      chartRef.current = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: rows.map((row) => row.name),
          datasets: [{
            label: 'Received Qty',
            data: rows.map((row) => Number(row.qty) || 0),
            backgroundColor: rows.map((_, index) => BAR_COLORS[index % BAR_COLORS.length]),
            borderRadius: 4,
            maxBarThickness: 28,
          }],
        },
        options: {
          indexAxis: 'y',
          responsive: true,
          maintainAspectRatio: false,
          animation: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: (item) => `${Number(item.raw || 0).toFixed(2)}`,
              },
            },
            datalabels: {
              display: (context) => Number(context.dataset.data[context.dataIndex] || 0) > 0,
              anchor: 'end',
              align: 'right',
              color: '#2c3e50',
              font: { size: 10, weight: '600' },
              formatter: (value) => Number(value).toFixed(1),
              clamp: true,
            },
          },
          scales: {
            x: {
              beginAtZero: true,
              grid: { color: 'rgba(0,0,0,0.05)' },
              ticks: {
                font: { size: 10 },
                callback: (value) => Number(value).toFixed(0),
              },
            },
            y: {
              grid: { display: false },
              ticks: { font: { size: 11 }, autoSkip: false },
            },
          },
          layout: {
            padding: { right: 28 },
          },
        },
        plugins: [ChartDataLabels],
      });

      try {
        chartRef.current.resize();
      } catch (e) {
        /* ignore */
      }
    });

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(frameId);
      destroyChart();
    };
  }, [rows, showChart, useGroupedView]);

  const canvasHeight = Math.max(140, Math.min(rows.length * 34 + 40, 720));

  const emptyMessage = (() => {
    if (view === 'month' && !plantId) return 'Select a plant to load financial year purchase.';
    if (view === 'month' && fyLoading) return 'Loading financial year purchase…';
    if (view === 'month' && fyError) return fyError;
    if (view === 'month' && totalQty <= 0) {
      return `No raw material purchase in ${monthAgg?.fyLabel || 'this financial year'} for the selected plant.`;
    }
    if (view === 'material' || view === 'day' || view === 'vendor') {
      return 'No raw material purchase data for this period.';
    }
    return 'No purchase data for this period.';
  })();

  const renderGroupedPeriodView = () => (
    <div className="purchase-period-groups" aria-label="Grouped material quantities">
      {groupedRows.map((group) => {
        const groupMaxQty = Math.max(0, ...group.items.map((item) => Number(item.qty) || 0));
        return (
          <div key={group.periodKey} className="purchase-period-group">
            <div className="purchase-period-header">
              <span className="purchase-period-title">{group.periodLabel}</span>
              <span className="purchase-period-total">{group.totalQty.toFixed(2)}</span>
            </div>
            <div className="purchase-period-materials">
              {group.items.map((item, index) => {
                const qty = Number(item.qty) || 0;
                const pct = groupMaxQty > 0 ? Math.max(2, (qty / groupMaxQty) * 100) : 0;
                const color = BAR_COLORS[index % BAR_COLORS.length];
                return (
                  <div key={item.sortKey || `${group.periodKey}-${item.material}`} className="purchase-period-material-row">
                    <span className="purchase-period-material-label" title={item.material}>
                      {item.material}
                    </span>
                    <div className="purchase-period-material-track">
                      <div
                        className="purchase-period-material-fill"
                        style={{
                          width: qty > 0 ? `${pct}%` : '0%',
                          backgroundColor: qty > 0 ? color : 'transparent',
                        }}
                      />
                    </div>
                    <span className="purchase-period-material-qty">{qty.toFixed(2)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="marketing-sales-chart">
      <div className="marketing-sales-chart-toolbar">
        <div className="marketing-sales-chart-title">
          <strong>{meta.title}</strong>
          <span className="marketing-sales-chart-sub">{meta.subtitle}</span>
        </div>

        <div className="marketing-sales-chart-views" role="tablist" aria-label="Purchase chart view">
          {VIEW_OPTIONS.map((option) => (
            <button
              key={option.id}
              type="button"
              role="tab"
              aria-selected={view === option.id}
              className={`marketing-sales-chart-view-btn ${view === option.id ? 'is-active' : ''}`}
              onClick={() => setView(option.id)}
            >
              {option.label}
            </button>
          ))}
        </div>

        <div className="marketing-sales-chart-controls">
          {view === 'month' && (
            <div className="marketing-sales-chart-fy">
              <label htmlFor="purchase-chart-fy">Financial year</label>
              <select
                id="purchase-chart-fy"
                className="marketing-sales-chart-fy-select"
                value={fyStartYear}
                onChange={(e) => setFyStartYear(Number(e.target.value))}
              >
                {fyOptions.map((option) => (
                  <option key={option.startYear} value={option.startYear}>
                    {option.label} (Apr–Mar)
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="marketing-sales-chart-total">
            {fyLoading && view === 'month' ? '…' : totalQty.toFixed(2)}
          </div>
        </div>
      </div>

      {!showChart ? (
        <p className="marketing-sales-chart-empty">{emptyMessage}</p>
      ) : useGroupedView ? (
        renderGroupedPeriodView()
      ) : (
        <div
          className="marketing-sales-chart-canvas-wrap"
          style={{ height: canvasHeight, minHeight: canvasHeight }}
        >
          <canvas ref={canvasRef} />
        </div>
      )}
      {view === 'month' && !fyLoading && fyError ? (
        <p className="marketing-sales-chart-empty">{fyError}</p>
      ) : null}
    </div>
  );
}

export default PurchaseChart;
